import { manipulateAsync, SaveFormat } from "expo-image-manipulator";
import type { ImagePickerAsset } from "expo-image-picker";

/**
 * Re-encode an image to JPEG. iOS photo-library assets are frequently HEIC
 * (and the camera can be, on some devices), which downstream consumers reject:
 * AWS Rekognition face-compare fails with `InvalidImageFormatException`, and the
 * AI document-extraction + PDF pipelines can't read HEIC either. Always
 * transcoding at capture time guarantees a JPEG lands in S3, whatever the
 * source format. `manipulateAsync` with no actions just re-encodes.
 */
export async function toJpeg(uri: string, compress = 0.85): Promise<string> {
    const result = await manipulateAsync(uri, [], { compress, format: SaveFormat.JPEG });
    return result.uri;
}

/**
 * Downscale an image so its longest edge is at most `maxDimension`, and always
 * re-encode to JPEG (HEIC-safe — see `toJpeg`). Resizing only happens when the
 * image exceeds the cap; the JPEG transcode happens either way.
 */
export async function resizeImageIfNeeded(
    asset: ImagePickerAsset,
    maxDimension: number,
    compress = 0.8
): Promise<string> {
    const { uri, width, height } = asset;
    //  Merge note: main early-returned `uri` when no downscale was needed,
    //  which also skipped the JPEG transcode. That predates the HEIC fix — iOS
    //  HEIC assets make Rekognition face-compare throw
    //  InvalidImageFormatException — so the transcode must happen either way.
    //  Main's missing-dimension guard is kept, but as a "cannot decide whether
    //  to downscale" case that still re-encodes, rather than a bail-out.
    const knowsSize = Boolean(width) && Boolean(height);
    const needsResize = knowsSize && (width > maxDimension || height > maxDimension);
    const isLandscape = width >= height;
    const actions = needsResize
        ? [{ resize: isLandscape ? { width: maxDimension } : { height: maxDimension } }]
        : [];
    const result = await manipulateAsync(uri, actions, { compress, format: SaveFormat.JPEG });
    return result.uri;
}

/**
 * Longest edge for an onboarding document photo.
 *
 * The document reader refuses anything over 5 MB, and a modern phone camera
 * clears that on its own: Mary Gatlin-Uzo's Hepatitis B record came off her
 * phone at 4284x5712 and 5.8 MB. She uploaded the same file ten times over
 * three hours on 31 August 2026 and every attempt was refused, with nothing
 * she could do about it — the app never downscaled, because onboarding was the
 * one upload path that never passed a `maxDimension`.
 *
 * 2400 is chosen to keep handwriting and lot numbers legible — her form at
 * 1800x2400 came out at 1.0 MB and read cleanly. Deliberately far larger than
 * the 1024 used for medication photos, where only the label matters.
 */
export const ONBOARDING_MAX_DIMENSION = 2400;
